// src/lib/routing/homeRoute.ts
//
// Home-route resolution for IDENTITY SWITCHES (impersonation start / stop).
// =========================================================================
// Starting or stopping impersonation used to `window.location.reload()` while
// keeping the CURRENT url. reload() preserves the hash, so an admin who was on
// employee Chuck's profile page and then impersonated Drew was left stranded on
// CHUCK'S page while acting as Drew (and, symmetrically, "Return to Admin" left
// the admin on whatever person-specific route the impersonated user had open).
// Switching identity must always land in a clean, neutral context — Home —
// never on the previously-viewed, person-specific route.
//
// Where "Home" resolves FROM the router:
//   - The sidebar "Home" nav item selects the `command` view
//     (App.tsx nav items: `{ key: 'command', label: 'Home' }`).
//   - `command` is also the router DEFAULT (getViewFromHash() falls back to
//     'command' for any unknown / empty hash).
//   - setActiveView maps the `command` view to the ROOT hash `#/`
//     (`window.location.hash = view === 'command' ? '/' : `/${seg}``).
//   - getHashSegments() parses `#/` to an EMPTY segment list, and
//     getViewFromHash() then resolves that back to `command`.
// So the neutral Home hash an identity switch navigates to is `/`.
//
// This module is the single TESTED source of that pick. App.tsx assigns
// `window.location.hash = homeRouteHash()` BEFORE the crm_impersonate
// localStorage set/remove + reload, so impersonation applies on a fresh Home
// load instead of on the stale, person-specific route. See docs/REGRESSIONS.md
// "Impersonation start/stop stranded the user on the previously-viewed route".

/** The view the sidebar "Home" nav item selects (also the router default). */
export const HOME_VIEW = 'command' as const

/**
 * Pure view→hash resolver mirroring App.tsx setActiveView's mapping: the
 * `command`/Home view is the root hash `/`; every other view is `/<seg>` (with
 * the `companies`→`clients` canonical-route alias). Kept as a testable
 * function so the Home-route pick can't silently drift from the router.
 */
export function viewToRouteHash(view: string): string {
  if (view === HOME_VIEW) return '/'
  const seg = view === 'companies' ? 'clients' : view
  return `/${seg}`
}

/**
 * The neutral Home hash an identity switch (impersonation start / stop)
 * navigates to before reloading. Assigning `window.location.hash` to this value
 * yields `#/`, which getViewFromHash() resolves back to the `command`/Home view.
 */
export function homeRouteHash(): string {
  return viewToRouteHash(HOME_VIEW)
}
